/**
 * EarnRatePage — Story 1.4 (Network Earn-Rate-Monitor UI)
 *
 * Referenz: v2-final/earn-rate.html — 4 Stat-Karten oben, darunter
 * zweispaltig: Rate-Verlauf (2/3) + Carrier-Mix/Erklaerbox (1/3).
 *
 * Contract-Realitaet (nichts erfinden):
 *  - Es gibt KEINEN netzwerkweiten Nova-Payer-Rate-Endpunkt im OpenAPI-
 *    Contract. Die "Current network rate"-Kachel rendert deshalb bewusst
 *    "—" (noValue) mit Hinweis; alle anderen Kacheln zeigen die ACCOUNT-
 *    Rate (mobile/analytics/summary, ungefiltert).
 *  - Der Verlaufs-Chart zeigt den ACCOUNT-Verlauf (daily_income x aktueller
 *    HNT-Kurs / GB je Tag) — als Hinweis im UI ausgewiesen.
 *  - Carrier-Mix kommt aus mobile/analytics/carrier_breakdown (ungefiltert,
 *    account-weit) — derselbe Endpunkt wie im Dashboard/Hotspot-Detail.
 *
 * Karten:
 *  1. Vier Stat-Karten: Netzwerkrate (Luecke), Account-Ø, Headroom zum Cap,
 *     Floor-Abstand — AC-3.
 *  2. Rate-Verlauf 30/90 Tage mit Floor-/Cap-Baendern (2/3-Spalte) — AC-1.
 *  3. Carrier-Mix (1/3-Spalte, aus echtem Endpunkt).
 *  4. "Wie die Rate entsteht": HIP-149 + HIP-143 (1/3-Spalte) — AC-2.
 */
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  CarrierBreakdownStack,
  formatMultiplier,
  formatNumber,
  formatRate,
  type CarrierSegment,
} from '../../components/ui'
import { useNetworkTransferSummary } from '../hotspots/useMobileTransfer'
import { useDailyIncome } from '../dashboard/useDailyIncome'
import { useMobileCarrierBreakdown } from '../dashboard/useMobileAnalytics'
import {
  buildRateHistory,
  hasReliableRateHistory,
  NOVA_CAP_MULTIPLIER,
  NOVA_CAP_USD,
  NOVA_FLOOR_MULTIPLIER,
  NOVA_FLOOR_USD,
} from './earnRateHistory'
import { RateHistoryChart } from './RateHistoryChart'
import './earn-rate.css'

type ChartRange = '30d' | '90d'

const NO_VALUE = '—'

export function EarnRatePage() {
  const { t } = useTranslation('earnrate')
  const [range, setRange] = useState<ChartRange>('30d')

  const summary24h = useNetworkTransferSummary('24h')
  const summaryRange = useNetworkTransferSummary(range)
  const income = useDailyIncome({ period: range })
  const carriers = useMobileCarrierBreakdown(range, 'day')

  const avg24h = summary24h.data?.avg_usd_per_gb ?? null
  const floorDistance = avg24h != null ? avg24h - NOVA_FLOOR_USD : null
  const headroomToCap = avg24h != null ? NOVA_CAP_USD - avg24h : null

  const historyLoading = summaryRange.isLoading || income.isLoading
  const historyError = summaryRange.isError || income.isError
  const historyPoints =
    summaryRange.data && income.data
      ? buildRateHistory(
          summaryRange.data.series,
          income.data,
          summaryRange.data.hnt_price_usd ?? null,
        )
      : []
  // F1(b): Kurve nur bei belastbarer Abdeckung — sonst Empty-State statt
  // einer Linie aus Legacy-Restwerten (siehe earnRateHistory.ts).
  const hasHistoryRates = hasReliableRateHistory(historyPoints)
  const priceMissing =
    summaryRange.data != null &&
    !(typeof summaryRange.data.hnt_price_usd === 'number' && summaryRange.data.hnt_price_usd > 0)

  const carrierSegments: CarrierSegment[] = (carriers.data?.carriers ?? []).map((c) => ({
    id: c.carrier_id,
    label: c.label,
    value: c.total_gb,
  }))

  return (
    <section data-testid="earn-rate-page">
      <header className="section-header">
        <div>
          <h1>{t('title')}</h1>
          <p>{t('subtitle')}</p>
        </div>
      </header>

      {/* Vier Stat-Karten (AC-3) — Reihenfolge wie Prototyp: Netzwerkrate,
          Account-Ø, Headroom zum Cap, Floor-Abstand. */}
      <div className="er-stat-grid" data-testid="earn-rate-stat-strip">
        <div className="card card-pad er-stat-card" data-testid="stat-payer-rate">
          <div className="stat-label">{t('stats.payerRate.label')}</div>
          <div className="stat-value num-md tnum">{NO_VALUE}</div>
          <div className="stat-delta muted">{t('stats.payerRate.missing')}</div>
        </div>

        <div className="card card-pad er-stat-card" data-testid="stat-avg24h">
          <div className="stat-label">{t('stats.avg24h.label')}</div>
          <div className="stat-value num-md tnum">
            {summary24h.isLoading ? (
              <span className="skeleton" style={{ display: 'inline-block', width: 64, height: 20 }} aria-busy="true" />
            ) : avg24h != null ? (
              formatRate(avg24h, '$', 3)
            ) : (
              NO_VALUE
            )}
          </div>
          <div className="stat-delta muted">
            {avg24h == null && summary24h.isError ? t('hero.error') : t('stats.avg24h.delta')}
          </div>
        </div>

        <div className="card card-pad er-stat-card" data-testid="stat-headroom-cap">
          <div className="stat-label">{t('stats.headroomCap.label')}</div>
          <div className="stat-value num-md tnum" style={{ color: 'var(--rate-cap)' }}>
            {headroomToCap != null
              ? `${headroomToCap >= 0 ? '+' : ''}${formatNumber(headroomToCap, 3)} $`
              : NO_VALUE}
          </div>
          <div className="stat-delta muted">
            {t('stats.headroomCap.delta', { cap: formatRate(NOVA_CAP_USD, '$', 3) })}
          </div>
        </div>

        <div className="card card-pad er-stat-card" data-testid="stat-floor-distance">
          <div className="stat-label">{t('stats.floorDistance.label')}</div>
          <div className="stat-value num-md tnum" style={{ color: 'var(--rate-floor)' }}>
            {floorDistance != null
              ? `${floorDistance >= 0 ? '+' : ''}${formatNumber(floorDistance, 3)} $`
              : NO_VALUE}
          </div>
          <div className="stat-delta muted">
            {floorDistance == null
              ? t('stats.floorDistance.deltaUnknown', {
                  floor: formatRate(NOVA_FLOOR_USD, '$', 3),
                })
              : floorDistance >= 0
                ? t('stats.floorDistance.deltaAbove', {
                    floor: formatRate(NOVA_FLOOR_USD, '$', 3),
                  })
                : t('stats.floorDistance.deltaBelow', {
                    floor: formatRate(NOVA_FLOOR_USD, '$', 3),
                  })}
          </div>
        </div>
      </div>

      {/* Contract-Luecke transparent machen (kein erfundener Netzwerkwert) */}
      <p className="muted er-gap-hint" data-testid="network-rate-gap-hint">
        {t('hero.networkGapHint')}
      </p>

      <div className="er-grid2">
        {/* Rate-Verlauf 30/90 Tage (AC-1) */}
        <div className="card card-pad er-chart-card" data-testid="earn-rate-chart-card">
          <div className="spread er-chart-head">
            <div>
              <h3>{t('chart.heading')}</h3>
              <p className="muted er-card-subtitle">{t('chart.subtitle')}</p>
            </div>
            <div className="segment" role="group" aria-label={t('chart.rangeAriaLabel')}>
              <button
                type="button"
                className={range === '30d' ? 'is-active' : ''}
                aria-pressed={range === '30d'}
                onClick={() => setRange('30d')}
                data-testid="range-30d"
              >
                {t('chart.range30')}
              </button>
              <button
                type="button"
                className={range === '90d' ? 'is-active' : ''}
                aria-pressed={range === '90d'}
                onClick={() => setRange('90d')}
                data-testid="range-90d"
              >
                {t('chart.range90')}
              </button>
            </div>
          </div>

          {historyLoading ? (
            <div className="skeleton" style={{ height: 320 }} aria-busy="true" />
          ) : historyError ? (
            <p className="muted er-empty" data-testid="rate-history-error">
              {t('chart.error')}
            </p>
          ) : priceMissing ? (
            <p className="muted er-empty" data-testid="rate-history-price-missing">
              {t('chart.priceMissing')}
            </p>
          ) : (
            // P1-Fix: Bänder/Linien (Floor+Cap, AC-1) sind IMMER als Referenz
            // zeichenbar, unabhaengig davon ob die Legacy-Kurve selbst genug
            // Abdeckung hat. Frueher ersetzte der !hasHistoryRates-Zweig den
            // gesamten Chart durch reinen Erklaertext — damit war die Chart-
            // Struktur nie pruefbar. Jetzt bleibt der Chart sichtbar, die
            // Kurve zeigt Luecken statt erfundener Werte (siehe
            // RateHistoryChart/earnRateHistory), und der Hinweis ergaenzt nur.
            <>
              <RateHistoryChart points={historyPoints} />
              {!hasHistoryRates && (
                <div className="er-empty" data-testid="rate-history-empty">
                  <p className="muted">{t('chart.empty')}</p>
                  <p className="muted">{t('chart.emptySourceHint')}</p>
                </div>
              )}
            </>
          )}

          <p className="muted er-gap-hint" data-testid="account-history-hint">
            {t('chart.accountFallbackNotice')}
          </p>
        </div>

        <div className="er-side-col">
          {/* Carrier-Mix (echter Endpunkt, account-weit) */}
          <div className="card card-pad" data-testid="earn-rate-carrier-mix">
            <h3 className="er-card-heading">{t('carriers.heading')}</h3>
            {carriers.isLoading ? (
              <div className="skeleton" style={{ height: 120 }} aria-busy="true" />
            ) : carriers.isError ? (
              <p className="muted er-empty" data-testid="carrier-mix-error">
                {t('carriers.error')}
              </p>
            ) : (
              <CarrierBreakdownStack
                segments={carrierSegments}
                formatValue={(v) => t('carriers.valueUnit', { value: formatNumber(v, 1) })}
                data-testid="earn-rate-carrier-stack"
              />
            )}
          </div>

          {/* HIP-149 / HIP-143 (AC-2) */}
          <div className="card card-pad" data-testid="earn-rate-hip-info">
            <h3 className="er-info-heading">{t('info.heading')}</h3>
            <div className="er-info-body">
              <p data-testid="hip-149-info">
                <Trans
                  t={t}
                  i18nKey="info.hip149"
                  components={{
                    strong: <strong className="er-info-strong" />,
                    mono: <span className="mono" />,
                  }}
                />
              </p>
              <p data-testid="hip-143-info">
                <Trans
                  t={t}
                  i18nKey="info.hip143"
                  values={{
                    floorMultiplier: formatMultiplier(NOVA_FLOOR_MULTIPLIER),
                    capMultiplier: formatMultiplier(NOVA_CAP_MULTIPLIER),
                    floor: formatNumber(NOVA_FLOOR_USD, 2),
                    cap: formatNumber(NOVA_CAP_USD, 2),
                  }}
                  components={{
                    strong: <strong className="er-info-strong" />,
                    floor: <span style={{ color: 'var(--rate-floor)' }} />,
                    cap: <span style={{ color: 'var(--rate-cap)' }} />,
                  }}
                />
              </p>
              <p>{t('info.dashboardNote')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
